import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAgentChatStore } from '../hooks/useAgentChatStore';
import { useDraggablePosition } from '../hooks/useDraggablePosition';
import { hasActiveConversation, isAgentVisibleOnPath } from '../services';
import { AgentStoreProvider } from '../store/AgentStoreProvider';
import { agentTheme } from '../styles/agentTheme';
import {
  colorAgentBorder,
  colorAgentDrawerBg,
  colorAgentHeaderBg,
  colorAgentTextPrimary,
  colorAgentTextSecondary,
  colorPrimarySeaGreen,
  fontSizeBase,
  fontWeightSemibold,
  space1,
  space3,
  zAgentFloating,
  zAgentSidebar,
  zAgentSnapPreview,
} from '../styles/tokens';
import { ChatInput } from './ChatInput';
import { FloatingLauncher } from './FloatingLauncher';
import { MessageList } from './MessageList';
import { WelcomeView } from './WelcomeView';

type DisplayMode = 'closed' | 'floating' | 'sidebar';
const FULLSCREEN_PATH = '/ai/hub';

const Header = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px ${space3};
  border-bottom: 1px solid ${colorAgentBorder};
  background-color: ${colorAgentHeaderBg};
  flex-shrink: 0;
`;

const HeaderLeft = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HeaderActions = styled(Box)`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const GradientIcon = styled(AutoAwesomeIcon)`
  font-size: 16px;
  color: ${colorPrimarySeaGreen};
  @supports (-webkit-background-clip: text) {
    background: linear-gradient(135deg, #00ffa0 0%, #00a99d 40%, #002169 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const HeaderTitle = styled(Typography)`
  font-weight: ${fontWeightSemibold};
  font-size: ${fontSizeBase};
  color: ${colorAgentTextPrimary};
`;

const HeaderIconButton = styled(IconButton)`
  padding: ${space1};
`;

const HeaderActionIcon = styled.span`
  font-size: 16px;
  color: ${colorAgentTextSecondary};
`;

const ChatBody = styled(Box)`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export interface AgentChatbotWidgetProps {
  companyCode: string;
}

function AgentChatbotWidgetInner({ companyCode }: AgentChatbotWidgetProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [displayMode, setDisplayMode] = useState<DisplayMode>('closed');
  const chat = useAgentChatStore();

  const handleOpen = useCallback(() => {
    chat.newChat();
    setDisplayMode('floating');
  }, [chat]);
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const {
    style: dragStyle,
    isDragging,
    isInSnapZone,
  } = useDraggablePosition({
    handleRef: headerRef,
    targetRef: panelRef,
    enabled: displayMode === 'floating' || displayMode === 'sidebar',
    snapZone: {
      threshold: 60,
      onSnap: () => setDisplayMode('sidebar'),
    },
    onDragStart: displayMode === 'sidebar' ? () => setDisplayMode('floating') : undefined,
  });

  if (!isAgentVisibleOnPath(pathname)) {
    return null;
  }

  const isCompanyReady = Boolean(companyCode);

  const handleSend = (message: string) => {
    if (!isCompanyReady) {
      return;
    }
    chat.sendMessageWs(message, companyCode);
  };

  const handleClose = useCallback(() => {
    setDisplayMode('closed');
  }, []);

  const handleFullscreen = useCallback(() => {
    setDisplayMode('closed');
    navigate(FULLSCREEN_PATH, { state: { returnUrl: pathname } });
  }, [navigate, pathname]);

  const shouldShowMessageList = hasActiveConversation(chat.messages, chat.isLoading);

  const headerContent = (
    <Header ref={headerRef} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
      <HeaderLeft>
        <GradientIcon />
        <HeaderTitle>AI アシスタント</HeaderTitle>
      </HeaderLeft>
      <HeaderActions>
        <Tooltip title="新しいチャット" placement="bottom">
          <HeaderIconButton size="small" onClick={chat.newChat}>
            <HeaderActionIcon as={EditNoteIcon} />
          </HeaderIconButton>
        </Tooltip>
        <Tooltip title="全画面表示" placement="bottom">
          <HeaderIconButton size="small" onClick={handleFullscreen}>
            <HeaderActionIcon as={FullscreenIcon} />
          </HeaderIconButton>
        </Tooltip>
        <Tooltip title="閉じる" placement="bottom">
          <HeaderIconButton size="small" onClick={handleClose}>
            <HeaderActionIcon as={CloseIcon} />
          </HeaderIconButton>
        </Tooltip>
      </HeaderActions>
    </Header>
  );

  const chatBody = (
    <>
      <ChatBody>
        {shouldShowMessageList ? (
          <MessageList
            messages={chat.messages}
            isLoading={chat.isLoading}
            error={chat.error}
            streamingStatus={chat.streamingStatus}
            currentToolName={chat.currentToolName}
            progressMessage={chat.progressMessage}
            skipTypewriter={chat.skipTypewriter}
            onRetry={chat.retry}
            onTypingComplete={chat.handleTypingComplete}
            approvalRequest={chat.approvalRequest}
            onApprove={chat.approveApproval}
            onReject={chat.rejectApproval}
            onEdit={chat.editApproval}
          />
        ) : (
          <WelcomeView onSuggestionClick={handleSend} />
        )}
      </ChatBody>
      <ChatInput onSend={handleSend} disabled={chat.isLoading || !isCompanyReady} />
    </>
  );

  return (
    <ThemeProvider theme={agentTheme}>
      {displayMode === 'closed' && <FloatingLauncher onClick={handleOpen} />}

      {(displayMode === 'floating' || displayMode === 'sidebar') && (
        <>
          <Box
            ref={panelRef}
            style={displayMode === 'floating' ? dragStyle : undefined}
            sx={
              displayMode === 'sidebar'
                ? {
                    position: 'fixed',
                    top: '40px',
                    right: 0,
                    width: 380,
                    height: 'calc(100vh - 40px)',
                    zIndex: zAgentSidebar,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: colorAgentDrawerBg,
                    borderLeft: `1px solid ${colorAgentBorder}`,
                  }
                : {
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    width: 380,
                    maxWidth: 'calc(100vw - 32px)',
                    height: 480,
                    maxHeight: 'calc(100vh - 72px)',
                    zIndex: zAgentFloating,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: colorAgentDrawerBg,
                    border: `1px solid ${colorAgentBorder}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                    overflow: 'hidden',
                  }
            }
          >
            {headerContent}
            {chatBody}
          </Box>
          {isDragging && isInSnapZone && (
            <Box
              sx={{
                position: 'fixed',
                top: 40,
                right: 0,
                width: 380,
                height: 'calc(100vh - 40px)',
                background: 'rgba(0, 150, 136, 0.06)',
                borderLeft: '2px dashed rgba(0, 150, 136, 0.4)',
                zIndex: zAgentSnapPreview,
                pointerEvents: 'none',
              }}
            />
          )}
        </>
      )}
    </ThemeProvider>
  );
}

export function AgentChatbotWidget({ companyCode }: AgentChatbotWidgetProps) {
  return (
    <AgentStoreProvider>
      <AgentChatbotWidgetInner companyCode={companyCode} />
    </AgentStoreProvider>
  );
}
